/*
 * Web push opt-in.
 *
 * Asks with our own banner first and only calls the browser's permission prompt
 * once the shopper has said yes. That ordering matters more than it looks: a
 * browser prompt refused is refused for good — the site can never ask again —
 * whereas our banner can be dismissed and shown another day.
 *
 * Nothing from Firebase is downloaded until someone accepts, so the ~40 KB SDK
 * never touches a page view that was not going to subscribe anyway.
 */
(function () {
  var CONFIG = window.plushPushConfig;
  if (!CONFIG || !CONFIG.vapidKey) return;

  // iOS only delivers push to sites installed to the home screen, so on a
  // normal iPhone visit there is nothing to offer.
  var supported =
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    typeof Notification !== 'undefined';
  if (!supported) return;

  var STORE_KEY = 'plush-push-state';
  var DISMISS_DAYS = 60;
  var DELAY_MS = 25000;

  function readState() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private windows throw; the banner simply shows again next time */
    }
  }

  // Merge rather than replace. The counters below live in the same record, and
  // the old writeState({ dismissedAt: ... }) calls would have wiped them.
  function patchState(partial) {
    var state = readState();
    for (var key in partial) {
      if (Object.prototype.hasOwnProperty.call(partial, key)) state[key] = partial[key];
    }
    writeState(state);
  }

  // Two places to look, because neither alone answers the question:
  //
  //   localStorage — per device, survives reloads, free, and readable from the
  //   console on any machine you can reach. Good for "did this fire at all".
  //
  //   fbq trackCustom — aggregates across every visitor in Events Manager, at
  //   no extra request, because plush-analytics already loads the Meta pixel
  //   (settings.meta_pixel_id is set; the GA4 field is not). The typeof guard
  //   is there for the day that setting is cleared, not because it is empty.
  //
  // DELAY_MS is 25 seconds and nobody has measured whether sessions last that
  // long. PushBannerShown is the number that decides it: if it collapses
  // against PushBannerEligible, the delay is too long, not the offer.
  function track(name) {
    try {
      var state = readState();
      state.counts = state.counts || {};
      state.counts[name] = (state.counts[name] || 0) + 1;
      state.counts.lastAt = Date.now();
      writeState(state);
    } catch (e) {
      /* counters are diagnostics; never let them break the banner */
    }
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', 'PushBanner' + name);
    } catch (e) {
      /* same */
    }
  }

  // Exposed so the counts can be read without digging through localStorage.
  window.plushPushStats = function () {
    return readState().counts || {};
  };

  function shouldAsk() {
    if (Notification.permission !== 'default') return false;
    var state = readState();
    if (state.subscribed) return false;
    if (state.dismissedAt && Date.now() - state.dismissedAt < DISMISS_DAYS * 864e5) return false;
    return true;
  }

  function buildBanner() {
    var wrap = document.createElement('div');
    wrap.className = 'plush-push';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-label', CONFIG.heading);
    wrap.innerHTML =
      '<div class="plush-push__text">' +
      '<strong class="plush-push__heading"></strong>' +
      '<span class="plush-push__body"></span>' +
      '</div>' +
      '<div class="plush-push__actions">' +
      '<button type="button" class="plush-push__no button button--secondary"></button>' +
      '<button type="button" class="plush-push__yes button"></button>' +
      '</div>';
    wrap.querySelector('.plush-push__heading').textContent = CONFIG.heading;
    wrap.querySelector('.plush-push__body').textContent = CONFIG.body;
    wrap.querySelector('.plush-push__yes').textContent = CONFIG.accept;
    wrap.querySelector('.plush-push__no').textContent = CONFIG.decline;
    return wrap;
  }

  function close(banner) {
    banner.classList.remove('is-open');
    setTimeout(function () { banner.remove(); }, 250);
  }

  async function subscribe() {
    var permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      track('Blocked');
      patchState({ dismissedAt: Date.now() });
      return;
    }

    var registration = await navigator.serviceWorker.register(CONFIG.serviceWorker);

    var appModule = await import(CONFIG.sdkApp);
    var messagingModule = await import(CONFIG.sdkMessaging);

    var app = appModule.initializeApp(CONFIG.firebase);
    var messaging = messagingModule.getMessaging(app);

    var token = await messagingModule.getToken(messaging, {
      vapidKey: CONFIG.vapidKey,
      serviceWorkerRegistration: registration
    });

    if (token) {
      // FCM holds the token list, so there is nothing of our own to store —
      // the flag is only here to stop the banner coming back.
      track('Subscribed');
      patchState({ subscribed: true, at: Date.now() });
    }
    return token;
  }

  function show() {
    if (!shouldAsk()) return;
    var banner = buildBanner();
    document.body.appendChild(banner);
    track('Shown');
    requestAnimationFrame(function () { banner.classList.add('is-open'); });

    banner.querySelector('.plush-push__no').addEventListener('click', function () {
      track('Declined');
      patchState({ dismissedAt: Date.now() });
      close(banner);
    });

    banner.querySelector('.plush-push__yes').addEventListener('click', async function () {
      track('Accepted');
      close(banner);
      try {
        await subscribe();
      } catch (e) {
        // A failed subscribe should never break the page it appeared on.
        patchState({ dismissedAt: Date.now() });
      }
    });
  }

  // Exposed so the banner can be summoned for testing without the wait.
  window.plushPushAsk = show;
  window.plushPushSubscribe = subscribe;

  if (shouldAsk()) {
    // Counted before the timer, not after it. Eligible minus Shown is exactly
    // the number of sessions that ended during the DELAY_MS wait, which is the
    // thing we cannot currently see.
    track('Eligible');
    setTimeout(show, DELAY_MS);
  }
})();
